import { useEffect, useRef, useState } from 'react';
import { Form, Table } from 'react-bootstrap';
import { MdAdd, MdDeleteOutline } from 'react-icons/md';
import {
  AlexaVisibilityQuestion,
  GetAlexaVisibilityQuestionsResponse
} from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { useAppSelector } from '@src/redux';
import { currentCompanySelector } from '@src/redux/company';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { Button } from '@Components/Button';
import { CopiedToast } from '@Components/CopiedToast';
import { Checkbox } from '@Components/FormCheck';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { Spinner } from '@Components/Spinner/Spinner';
import { PAGE_NAME } from '../alexa-visibility-constants';
import {
  DraftQuestion,
  ENTITY_NAME,
  NEW_QUESTION,
  QUESTION_SET_OPTIONS
} from '../alexa-visibility-questions-constants';
import {
  countEnabledBySet,
  fromDraftRows,
  getDraftErrors,
  isDraftChanged,
  toDraftRows
} from '../alexa-visibility-questions-utils';
import {
  useAlexaVisibilityQuestions,
  useSaveAlexaVisibilityQuestions
} from '../hooks/useAlexaVisibilityQuestions';

interface QuestionsTabProps {
  brand: string;
}

export const QuestionsTab = ({ brand }: QuestionsTabProps) => {
  const company = useAppSelector(currentCompanySelector);
  const { name: companyName } = useCompanyInfo();
  const setError = useSetError();
  const { data, isLoading, error } = useAlexaVisibilityQuestions(company, brand);
  // Bumped after each successful save so the editor remounts with the freshly saved list.
  // Keying on a save counter (rather than the fetch time) means a background refetch can't
  // wipe unsaved edits.
  const [saveCount, setSaveCount] = useState(0);
  const [toastMessage, setToastMessage] = useState<string>();

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load questions",
        message: `Failed to load Alexa Visibility questions: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  const handleSaved = (questionCount: number) => {
    const emptyMessage = brand
      ? `Saved. ${brand} now uses the company-wide questions.`
      : 'Saved. This client now uses the built-in default questions.';
    setSaveCount(count => count + 1);
    setToastMessage(
      questionCount === 0
        ? emptyMessage
        : `Saved ${conditionallyPluralizeWithCount(questionCount, 'question')}.`
    );
  };

  if (!data) return isLoading ? <Spinner /> : undefined;

  return (
    <>
      <QuestionsEditor
        key={`${company}-${brand}-${saveCount}`}
        company={company}
        companyName={companyName}
        brand={brand}
        saved={data}
        onSaved={handleSaved}
      />
      <CopiedToast
        show={Boolean(toastMessage)}
        onClose={() => setToastMessage(undefined)}
        message={toastMessage ?? ''}
        delay={4000}
      />
    </>
  );
};

interface QuestionsEditorProps {
  company: string;
  brand: string;
  companyName: string;
  saved: GetAlexaVisibilityQuestionsResponse;
  onSaved: (questionCount: number) => void;
}

const QuestionsEditor = ({ company, companyName, brand, saved, onSaved }: QuestionsEditorProps) => {
  const [rows, setRows] = useState<DraftQuestion[]>(() => toDraftRows(saved.questions));
  const nextRowId = useRef(0);
  const save = useSaveAlexaVisibilityQuestions();

  const errors = getDraftErrors(rows);
  const canSave = (saved.usingDefaults || isDraftChanged(saved.questions, rows)) && !errors.length;
  const counts = countEnabledBySet(fromDraftRows(rows));

  const updateRow = (rowId: string, patch: Partial<AlexaVisibilityQuestion>) =>
    setRows(current => current.map(row => (row.rowId === rowId ? { ...row, ...patch } : row)));

  const addRow = () => {
    nextRowId.current += 1;
    setRows(current => [...current, { ...NEW_QUESTION, rowId: `new-${nextRowId.current}` }]);
  };

  const removeRow = (rowId: string) =>
    setRows(current => current.filter(row => row.rowId !== rowId));

  const handleSave = () => {
    const questions = fromDraftRows(rows);
    save.mutate(
      { company, brand: brand || undefined, questions },
      {
        onSuccess: () => {
          Mixpanel.track(MxE.RecordSaved, { page: PAGE_NAME, entity: ENTITY_NAME });
          onSaved(questions.length);
        }
      }
    );
  };

  return (
    <div className='aars-config-editor'>
      <div className='aars-config-scope' data-testid='questions-scope-label'>
        {brand ? (
          <>
            Editing saved questions for <strong>{brand}</strong>. Changes only apply to this brand;
            other brands under {companyName || company} keep their own questions.
          </>
        ) : (
          <>
            Editing the company-wide questions for <strong>{companyName || company}</strong>. Pick a
            Target brand to give it its own list.
          </>
        )}
      </div>
      <div className='aars-config-header'>
        <div className='aars-config-summary'>
          {saved.usingDefaults && (
            <div className='aars-config-notice'>
              {brand
                ? `${brand} has no saved questions of its own, so runs use the company-wide list (or the built-in defaults if there is none). Saving creates a ${brand}-specific copy you can edit.`
                : 'This client has no saved questions, so runs use the built-in default list. Saving creates a client-specific copy you can edit.'}
            </div>
          )}
          {rows.length === 0 && !saved.usingDefaults && (
            <div className='aars-config-notice'>
              {brand
                ? `With no questions, saving removes ${brand}'s own list and runs go back to using the company-wide list.`
                : "With no questions, saving removes this client's configuration and runs go back to using the built-in default list."}
            </div>
          )}
          <span>
            Enabled: {counts.all} total · {counts.a} in Set A · {counts.b} in Set B
          </span>
        </div>
        <Button
          data-testid='save-questions-button'
          onClick={handleSave}
          disabled={!canSave}
          loading={save.isPending}
        >
          Save Questions
        </Button>
      </div>
      {errors.length > 0 && (
        <ul className='aars-config-errors' data-testid='question-errors'>
          {errors.map(message => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
      <Table className='aars-config-table'>
        <thead>
          <tr>
            <th>Enabled</th>
            <th>Question</th>
            <th>Category</th>
            <th>Persona</th>
            <th>Query set</th>
            <th aria-label='Remove' />
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <QuestionRow key={row.rowId} row={row} onChange={updateRow} onRemove={removeRow} />
          ))}
        </tbody>
      </Table>
      <div>
        <Button leadingElement={<MdAdd />} onClick={addRow} data-testid='add-question-button'>
          Add Question
        </Button>
      </div>
    </div>
  );
};

interface QuestionRowProps {
  row: DraftQuestion;
  onChange: (rowId: string, patch: Partial<AlexaVisibilityQuestion>) => void;
  onRemove: (rowId: string) => void;
}

const QuestionRow = ({ row, onChange, onRemove }: QuestionRowProps) => (
  <tr data-testid='question-row'>
    <td>
      <Checkbox
        aria-label='Enabled'
        checked={row.enabled}
        onChange={e => onChange(row.rowId, { enabled: e.target.checked })}
      />
    </td>
    <td>
      <Form.Control
        aria-label='Question'
        value={row.query}
        onChange={e => onChange(row.rowId, { query: e.target.value })}
      />
    </td>
    <td>
      <Form.Control
        aria-label='Category'
        value={row.category ?? ''}
        onChange={e => onChange(row.rowId, { category: e.target.value })}
      />
    </td>
    <td>
      <Form.Control
        aria-label='Persona'
        value={row.persona ?? ''}
        onChange={e => onChange(row.rowId, { persona: e.target.value })}
      />
    </td>
    <td>
      <Dropdown
        value={row.querySet}
        options={QUESTION_SET_OPTIONS}
        onChange={value => onChange(row.rowId, { querySet: value })}
      />
    </td>
    <td>
      <IconButton aria-label='Remove question' onClick={() => onRemove(row.rowId)}>
        <MdDeleteOutline />
      </IconButton>
    </td>
  </tr>
);
